/**
 * ChatThread — the right pane of the Mobile Forensics view.
 *
 * A read-only reconstruction of one thread, rendered the way the device
 * showed it: the owner's messages as primary bubbles on the right, the
 * counterpart's on the left, day separators between calendar days, sender
 * handles inside group chats. Evidence framing stays visible — timestamps
 * and identities are monospaced and verbatim, attachments show the recorded
 * path's basename, and nothing here is editable.
 */

import {
  ArrowLeftIcon,
  ArrowUpIcon,
  FlagIcon,
  HighlighterIcon,
  Loader2,
  PaperclipIcon,
  PauseIcon,
  SparklesIcon,
  XIcon,
} from 'lucide-react'
import {
  type FC,
  Fragment,
  memo,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { CommsConversation, CommsExcerpt, CommsMessage } from '@/services/api/comms'
import { cn } from '@/utils/cn'
import {
  appLabel,
  attachmentName,
  bubbleTime,
  conversationTitle,
  DIRECTION_OUT,
  dayLabel,
  isGroup,
  sittingGapHours,
  sittings,
  startsNewDay,
} from './comms-format'

/** ADR-0024 size cap — mirrors the server's, so the UI refuses before the API does. */
const MAX_EXCERPT_MESSAGES = 200

/** A promotable message range the analyst has framed by clicking two bubbles. */
export interface ExcerptRange {
  startMessageId: string
  endMessageId: string
  note: string
}

interface ChatThreadProps {
  conversation: CommsConversation
  /** Phone only: leave the thread for the conversation list. The control is
   * hidden from md up, where both panes are on screen. */
  onBack?: () => void
  /** Full loaded window, oldest → newest. */
  messages: CommsMessage[]
  hasOlder: boolean
  loadingOlder: boolean
  onLoadOlder: () => void
  /** Message whose provenance panel is open, if any. */
  selectedId: string | null
  onSelect: (message: CommsMessage) => void
  /** Promote a contiguous range into the graph (ADR-0024). Absent → the thread
   * stays read-only with no promotion affordance. */
  onPromote?: (range: ExcerptRange) => void
  /** Publish several ranges in one go — used when an over-cap selection is
   * split along its sitting boundaries. */
  onPromoteMany?: (ranges: ExcerptRange[]) => void
  promoting?: boolean
  /** False when the extraction's tier carries no message bodies: there is
   * nothing to promote, and saying so is a statement about the acquisition
   * method, never about the person (SC-013). */
  promotable?: boolean
  /** Why promotion is unavailable, shown in place of the affordance. */
  notPromotableReason?: string
  /** Ranges already promoted from this thread — drawn as gutter markers so
   * published evidence stays visible where it was taken from. */
  excerpts?: CommsExcerpt[]
  /** Run the standard extraction chain over a promoted excerpt (origin: mined). */
  onMine?: (excerpt: CommsExcerpt) => void
  mining?: boolean
  /** The promoted range the thread was opened on from the graph: the view
   * lands on it and it stays highlighted until the analyst moves on. */
  focusExcerptId?: string | null
}

const DaySeparator: FC<{ iso: string }> = ({ iso }) => {
  const { i18n } = useTranslation()
  return (
    <div className="my-3 flex items-center gap-3">
      <span className="h-px flex-1 bg-border/60" />
      <span className="rounded-full border border-border bg-background px-2.5 py-0.5 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        {dayLabel(iso, i18n.language)}
      </span>
      <span className="h-px flex-1 bg-border/60" />
    </div>
  )
}

const Bubble = memo(function Bubble({
  message,
  showSender,
  active,
  inRange,
  onActivate,
}: {
  message: CommsMessage
  showSender: boolean
  /** Provenance panel open, or a boundary of the framed range. */
  active: boolean
  /** Inside the framed promotion range (highlighted, not a boundary). */
  inRange: boolean
  onActivate: (message: CommsMessage) => void
}) {
  const { t, i18n } = useTranslation()
  const outgoing = message.direction === DIRECTION_OUT
  return (
    <div className={cn('flex w-full', outgoing ? 'justify-end' : 'justify-start')}>
      {/* A bubble opens its provenance panel, or — in promote mode — sets a
          boundary of the range. A button, not a div handler, so the thread
          stays keyboard-navigable: every message reachable by Tab, Enter. */}
      <button
        type="button"
        onClick={() => onActivate(message)}
        aria-pressed={active}
        className={cn(
          'max-w-[85%] cursor-pointer rounded-2xl px-3 py-1.5 text-left text-sm shadow-sm outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring md:max-w-[72%]',
          outgoing
            ? 'rounded-br-sm bg-primary text-primary-foreground'
            : 'rounded-bl-sm border border-border/60 bg-muted text-foreground',
          inRange && 'ring-2 ring-ring/40',
          active && 'ring-2 ring-ring'
        )}
      >
        {showSender && !outgoing && (
          <p className="mb-0.5 font-mono text-2xs font-medium text-muted-foreground">
            {message.sender}
          </p>
        )}
        {message.attachmentRef && (
          <p
            className={cn(
              'mb-1 flex items-center gap-1.5 rounded-md border border-dashed px-2 py-1 font-mono text-2xs',
              outgoing ? 'border-primary-foreground/30' : 'border-border'
            )}
          >
            <PaperclipIcon className="size-3 shrink-0" />
            <span className="truncate">{attachmentName(message.attachmentRef)}</span>
          </p>
        )}
        {message.body ? (
          <p className="whitespace-pre-wrap break-words">{message.body}</p>
        ) : (
          !message.attachmentRef && (
            <p
              className={cn(
                'italic',
                outgoing ? 'text-primary-foreground/70' : 'text-muted-foreground'
              )}
            >
              {t(`comms.msgType.${message.msgType}`, { defaultValue: t('comms.thread.noBody') })}
            </p>
          )
        )}
        <p
          className={cn(
            'mt-0.5 text-right font-mono text-2xs',
            outgoing ? 'text-primary-foreground/70' : 'text-muted-foreground'
          )}
        >
          {bubbleTime(message.sentAt, i18n.language)}
        </p>
      </button>
    </div>
  )
})

const ThreadHeader: FC<{
  conversation: CommsConversation
  canPromote: boolean
  blockedReason?: string
  promoteMode: boolean
  onTogglePromote: () => void
  onBack?: () => void
}> = ({ conversation, canPromote, blockedReason, promoteMode, onTogglePromote, onBack }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-1 gap-y-1.5 border-b border-border px-2 py-2 md:px-4 md:py-2.5 lg:flex-nowrap lg:gap-3">
      {onBack && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label={t('comms.tabs.chats')}
          className="size-9 shrink-0 md:hidden"
        >
          <ArrowLeftIcon className="size-4" />
        </Button>
      )}
      <div className="min-w-0 max-lg:flex-1">
        <h2 className="truncate text-sm font-semibold text-foreground">
          {conversationTitle(conversation)}
        </h2>
        <p className="truncate font-mono text-2xs text-muted-foreground">
          {conversation.participants.join(' · ')}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5 max-lg:w-full max-lg:flex-wrap max-md:px-2">
        {canPromote ? (
          <Button
            variant={promoteMode ? 'secondary' : 'outline'}
            size="sm"
            className="h-8 gap-1.5 text-2xs md:h-7"
            onClick={onTogglePromote}
          >
            <HighlighterIcon className="size-3.5" />
            {t('comms.promote.select')}
          </Button>
        ) : (
          blockedReason && (
            <Badge variant="outline" className="text-2xs text-muted-foreground">
              {blockedReason}
            </Badge>
          )
        )}
        <Badge variant="outline" className="text-2xs">
          {appLabel(conversation.app, t)}
        </Badge>
        <Badge variant="secondary" className="font-mono text-2xs">
          {t('comms.thread.messageCount', { count: conversation.messageCount })}
        </Badge>
      </div>
    </div>
  )
}

/** The break between two sittings: how long the thread went quiet, then the
 * next sitting's span. In promote mode it doubles as a one-click way to frame
 * exactly that sitting — the unit an exchange naturally has. */
const SittingDivider: FC<{
  quietHours: number | null
  span: { from: string | null; to: string | null }
  count: number
  promoteMode: boolean
  onSelectSitting: () => void
}> = ({ quietHours, span, count, promoteMode, onSelectSitting }) => {
  const { t, i18n } = useTranslation()
  const quiet =
    quietHours === null
      ? null
      : quietHours >= 48
        ? t('comms.sitting.quietDays', {
            count: Math.round(quietHours / 24),
          })
        : t('comms.sitting.quietHours', {
            count: Math.round(quietHours),
          })
  return (
    <div
      className={cn(
        'my-3 flex items-center gap-2',
        // With the select button the row no longer fits a phone (or the
        // narrow thread pane below lg): let it wrap, centred, without the rules.
        promoteMode && 'max-lg:flex-wrap max-lg:justify-center'
      )}
    >
      <span className={cn('h-px flex-1 bg-border', promoteMode && 'max-lg:hidden')} />
      {quiet && (
        <span className="flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-2xs font-medium text-muted-foreground">
          <PauseIcon className="size-3" />
          {quiet}
        </span>
      )}
      <span className="rounded-full border border-border bg-background px-2.5 py-0.5 font-mono text-2xs text-muted-foreground">
        {span.from ? dayLabel(span.from, i18n.language) : '—'} ·{' '}
        {t('comms.sitting.count', { count })}
      </span>
      {promoteMode && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0 text-2xs md:h-6"
          onClick={onSelectSitting}
        >
          {t('comms.sitting.select')}
        </Button>
      )}
      <span className={cn('h-px flex-1 bg-border', promoteMode && 'max-lg:hidden')} />
    </div>
  )
}

/** What this thread has already contributed to the graph. Jumping to a range
 * is the reverse of promoting it — the analyst can always get back to the
 * messages behind a node. */
const PublishedStrip: FC<{
  excerpts: CommsExcerpt[]
  onJump: (excerpt: CommsExcerpt) => void
  onMine?: (excerpt: CommsExcerpt) => void
  mining?: boolean
  focusExcerptId?: string | null
}> = ({ excerpts, onJump, onMine, mining, focusExcerptId }) => {
  const { t } = useTranslation()
  if (excerpts.length === 0) return null
  return (
    <div className="flex items-center gap-2 overflow-x-auto border-b border-border bg-muted/30 px-4 py-1.5">
      <span className="shrink-0 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('comms.published.title')}
      </span>
      {excerpts.map(excerpt => (
        <Button
          key={excerpt.id}
          variant={excerpt.excerptId === focusExcerptId ? 'secondary' : 'outline'}
          size="sm"
          className="h-8 shrink-0 gap-1.5 text-2xs md:h-6"
          onClick={() => onJump(excerpt)}
        >
          <FlagIcon className="size-3 text-primary" />
          <span className="max-w-40 truncate">{excerpt.note || t('comms.published.untitled')}</span>
          <span className="font-mono text-muted-foreground">{excerpt.messageCount}</span>
        </Button>
      ))}
      {onMine && excerpts.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 gap-1.5 text-2xs md:h-6"
          onClick={() => onMine(excerpts[excerpts.length - 1])}
          loading={mining}
        >
          <SparklesIcon className="size-3" />
          {t('comms.published.mine')}
        </Button>
      )}
    </div>
  )
}

/** Sticky bar shown while a range is framed: extent, cap, note, publish. */
const PromoteBar: FC<{
  count: number
  span: { from: string | null; to: string | null }
  overCap: boolean
  sittingCount: number
  note: string
  onNote: (note: string) => void
  onPublish: () => void
  onSplit: () => void
  onCancel: () => void
  publishing: boolean
}> = ({
  count,
  span,
  overCap,
  sittingCount,
  note,
  onNote,
  onPublish,
  onSplit,
  onCancel,
  publishing,
}) => {
  const { t, i18n } = useTranslation()
  return (
    <div className="flex flex-col items-stretch gap-2 border-t border-border bg-background px-3 py-2 md:px-4 lg:flex-row lg:items-center">
      {/* Two rows below lg, where the thread pane is phone-narrow (extent,
          then note + actions); from lg both wrappers dissolve into the single bar. */}
      <div className="flex items-center gap-2 lg:contents">
        <Badge
          variant={overCap ? 'destructive' : 'secondary'}
          className="shrink-0 font-mono text-2xs"
        >
          {count}/{MAX_EXCERPT_MESSAGES}
        </Badge>
        <span className="min-w-0 font-mono text-2xs text-muted-foreground lg:shrink-0">
          {span.from ? dayLabel(span.from, i18n.language) : '—'} →{' '}
          {span.to ? dayLabel(span.to, i18n.language) : '—'}
        </span>
      </div>
      <div className="flex items-center gap-2 lg:contents">
        {overCap ? (
          <>
            <span className="flex-1 text-2xs text-muted-foreground">
              {t('comms.promote.overCap')}
            </span>
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={onSplit}>
              {t('comms.promote.split', {
                count: sittingCount,
              })}
            </Button>
          </>
        ) : (
          <>
            <Input
              value={note}
              onChange={event => onNote(event.target.value)}
              placeholder={t('comms.promote.note')}
              className="h-9 flex-1 text-xs md:h-8"
            />
            <Button
              size="sm"
              className="h-9 shrink-0 md:h-8"
              onClick={onPublish}
              loading={publishing}
            >
              {t('comms.promote.publish')}
            </Button>
          </>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="size-9 shrink-0 p-0 md:size-8"
          onClick={onCancel}
        >
          <XIcon className="size-4" />
        </Button>
      </div>
    </div>
  )
}

/** Keeps a thread that sits on its newest message there when its box gets
 * shorter. The strips above the thread mount when their own queries answer —
 * after the landing, as often as not — and take their height out of the box,
 * which left the newest message (or the search hit the thread was opened on)
 * under the fold. The published strip arrives together with the range labels
 * inside the thread, so "sat on the newest" is judged against the sizes from
 * before the change, which is why the rows are observed too. Paused in promote
 * mode, where the bar that appears below the thread is the analyst's own doing. */
function useStayAtNewest(
  scrollRef: RefObject<HTMLDivElement | null>,
  rowsRef: RefObject<HTMLDivElement | null>,
  paused: boolean
) {
  useEffect(() => {
    const el = scrollRef.current
    const rows = rowsRef.current
    if (!el || !rows || paused || typeof ResizeObserver === 'undefined') return
    let boxHeight = el.clientHeight
    let contentHeight = el.scrollHeight
    const observer = new ResizeObserver(() => {
      // Not laid out (its pane is hidden on a phone): nothing to hold on to.
      if (el.clientHeight === 0) return
      const wasAtNewest = contentHeight - el.scrollTop - boxHeight <= 2
      const shrank = el.clientHeight < boxHeight
      boxHeight = el.clientHeight
      contentHeight = el.scrollHeight
      if (shrank && wasAtNewest) el.scrollTop = el.scrollHeight
    })
    observer.observe(el)
    observer.observe(rows)
    return () => observer.disconnect()
  }, [scrollRef, rowsRef, paused])
}

export const ChatThread: FC<ChatThreadProps> = ({
  conversation,
  onBack,
  messages,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  selectedId,
  onSelect,
  onPromote,
  onPromoteMany,
  promoting = false,
  promotable = true,
  notPromotableReason,
  excerpts = [],
  onMine,
  mining = false,
  focusExcerptId = null,
}) => {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const rowsRef = useRef<HTMLDivElement>(null)
  const group = isGroup(conversation)

  // Promotion selection is pure UI state: two clicks frame a contiguous range
  // by message index, so a gap can never appear (ADR-0024: range only). anchor
  // set = first click; a second completes it; a third restarts.
  const [promoteMode, setPromoteMode] = useState(false)
  const [range, setRange] = useState<{ anchor: number; focus: number; complete: boolean } | null>(
    null
  )
  const [note, setNote] = useState('')
  useStayAtNewest(scrollRef, rowsRef, promoteMode)

  const indexOf = useMemo(() => {
    const map = new Map<string, number>()
    messages.forEach((message, index) => map.set(message.messageId, index))
    return map
  }, [messages])

  const bounds = range
    ? { lo: Math.min(range.anchor, range.focus), hi: Math.max(range.anchor, range.focus) }
    : null
  const count = bounds ? bounds.hi - bounds.lo + 1 : 0
  const overCap = count > MAX_EXCERPT_MESSAGES

  // Sittings drive the dividers, the one-click "select sitting", and the
  // sub-ranges an over-cap selection is split along.
  const sittingRanges = useMemo(() => sittings(messages), [messages])
  const sittingAt = useMemo(
    () => new Map(sittingRanges.map((sitting, index) => [sitting.start, index])),
    [sittingRanges]
  )
  const selectedSittings = bounds
    ? sittingRanges.filter(sitting => sitting.start <= bounds.hi && sitting.end >= bounds.lo)
    : []

  // Which excerpt (if any) each message was published in — the gutter marker
  // keys off message ids, so it survives paging and re-ordering.
  const publishedIn = useMemo(() => {
    const map = new Map<string, CommsExcerpt>()
    for (const excerpt of excerpts) {
      for (const id of excerpt.messageIds) map.set(id, excerpt)
    }
    return map
  }, [excerpts])

  const rangeOf = (lo: number, hi: number): ExcerptRange => ({
    startMessageId: messages[lo].messageId,
    endMessageId: messages[hi].messageId,
    note: note.trim(),
  })

  const resetPromotion = () => {
    setPromoteMode(false)
    setRange(null)
    setNote('')
  }

  const pickBoundary = (message: CommsMessage) => {
    const index = indexOf.get(message.messageId)
    if (index === undefined) return
    setRange(current =>
      current && !current.complete
        ? { anchor: current.anchor, focus: index, complete: true }
        : { anchor: index, focus: index, complete: false }
    )
  }

  /** Scroll the thread to a published range and flash its first message. The
   * range may be outside the loaded window; the caller can still page to it. */
  const jumpToExcerpt = (excerpt: CommsExcerpt) => {
    const target = document.getElementById(`msg-${excerpt.messageIds[0]}`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const selectSitting = (sitting: { start: number; end: number }) => {
    setPromoteMode(true)
    setRange({ anchor: sitting.start, focus: sitting.end, complete: true })
  }

  const publish = () => {
    if (!bounds || !onPromote || overCap) return
    onPromote(rangeOf(bounds.lo, bounds.hi))
    resetPromotion()
  }

  /** An over-cap selection becomes one excerpt per sitting it spans — each
   * clipped to the selection, so nothing outside what was framed is promoted. */
  const splitBySitting = () => {
    if (!bounds || !onPromoteMany) return
    onPromoteMany(
      selectedSittings.map(sitting =>
        rangeOf(Math.max(sitting.start, bounds.lo), Math.min(sitting.end, bounds.hi))
      )
    )
    resetPromotion()
  }

  // Day boundaries change only when the window changes — not per render.
  const rows = useMemo(
    () =>
      messages.map((message, index) => ({
        message,
        newDay: !!message.sentAt && startsNewDay(messages[index - 1], message),
      })),
    [messages]
  )

  // Land at the newest message when the thread opens; older pages prepend
  // above and must NOT yank the view, so only the conversation id triggers it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll only on thread switch / first fill
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [conversation.conversationId, messages.length > 0])

  // Opened from the graph: land on the promoted range instead of the newest
  // message, once its first message is in the loaded window. Once per focus,
  // so paging older messages afterwards does not yank the view back.
  const focusedRef = useRef<string | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: jump once per focused excerpt
  useEffect(() => {
    if (!focusExcerptId || focusedRef.current === focusExcerptId) return
    const excerpt = excerpts.find(e => e.excerptId === focusExcerptId)
    if (!excerpt || !indexOf.has(excerpt.messageIds[0])) return
    focusedRef.current = focusExcerptId
    jumpToExcerpt(excerpt)
  }, [focusExcerptId, excerpts, indexOf])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ThreadHeader
        conversation={conversation}
        canPromote={!!onPromote && promotable}
        blockedReason={promotable ? undefined : notPromotableReason}
        promoteMode={promoteMode}
        onTogglePromote={() => (promoteMode ? resetPromotion() : setPromoteMode(true))}
        onBack={onBack}
      />
      <PublishedStrip
        excerpts={excerpts}
        onJump={jumpToExcerpt}
        onMine={onMine}
        mining={mining}
        focusExcerptId={focusExcerptId}
      />
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto bg-muted/20 px-3 py-3 md:px-4">
        {hasOlder && (
          <div className="mb-2 flex justify-center">
            <Button variant="outline" size="sm" onClick={onLoadOlder} loading={loadingOlder}>
              {!loadingOlder && <ArrowUpIcon className="size-3.5" />}
              {t('comms.thread.loadOlder')}
            </Button>
          </div>
        )}
        {messages.length === 0 && !loadingOlder && (
          <p className="py-8 text-center text-xs text-muted-foreground">
            {t('comms.thread.empty')}
          </p>
        )}
        <div ref={rowsRef} className="flex flex-col gap-1">
          {rows.map(({ message, newDay }, index) => {
            const isBoundary = !!bounds && (index === bounds.lo || index === bounds.hi)
            const inRange = !!bounds && index >= bounds.lo && index <= bounds.hi
            const sittingIndex = sittingAt.get(index)
            const sitting = sittingIndex === undefined ? null : sittingRanges[sittingIndex]
            const published = publishedIn.get(message.messageId)
            const startsExcerpt = published?.messageIds[0] === message.messageId
            return (
              <Fragment key={message.messageId}>
                {sitting ? (
                  <SittingDivider
                    quietHours={sittingGapHours(messages[index - 1], message)}
                    span={{
                      from: messages[sitting.start].sentAt,
                      to: messages[sitting.end].sentAt,
                    }}
                    count={sitting.end - sitting.start + 1}
                    promoteMode={promoteMode}
                    onSelectSitting={() => selectSitting(sitting)}
                  />
                ) : (
                  newDay && message.sentAt && <DaySeparator iso={message.sentAt} />
                )}
                <div
                  id={`msg-${message.messageId}`}
                  className={cn(
                    'relative',
                    published && 'border-l-2 border-primary/60 pl-2',
                    published?.excerptId === focusExcerptId &&
                      'rounded-r-md border-primary bg-primary/5',
                    startsExcerpt && 'mt-2'
                  )}
                >
                  {startsExcerpt && published && (
                    <p className="mb-1 flex items-center gap-1.5 pl-1 text-2xs text-muted-foreground">
                      <FlagIcon className="size-3 shrink-0 text-primary" />
                      <span className="truncate">
                        {published.note || t('comms.published.untitled')}
                      </span>
                      <span className="shrink-0 font-mono">
                        · {t('comms.sitting.count', { count: published.messageCount })}
                      </span>
                      {published.excerptId === focusExcerptId && (
                        <span className="shrink-0 text-primary">
                          · {t('comms.evidence.focused')}
                        </span>
                      )}
                    </p>
                  )}
                  <Bubble
                    message={message}
                    showSender={group}
                    active={promoteMode ? isBoundary : message.messageId === selectedId}
                    inRange={promoteMode && inRange && !isBoundary}
                    onActivate={promoteMode ? pickBoundary : onSelect}
                  />
                </div>
              </Fragment>
            )
          })}
        </div>
        {loadingOlder && messages.length === 0 && (
          <div className="flex justify-center py-8">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      {promoteMode && bounds && (
        <PromoteBar
          count={count}
          span={{ from: messages[bounds.lo].sentAt, to: messages[bounds.hi].sentAt }}
          overCap={overCap}
          sittingCount={selectedSittings.length}
          note={note}
          onNote={setNote}
          onPublish={publish}
          onSplit={splitBySitting}
          onCancel={resetPromotion}
          publishing={promoting}
        />
      )}
    </div>
  )
}
